import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authDao } from 'shared/dao/authDao';
import {
  IForgotPasswordRequestPayload,
  IResetPasswordRequestPayload,
  ISignInRequestPayload,
  ISignUpRequestPayload,
  IUser,
} from 'shared/interfaces/IUser';
import { ls } from 'shared/utils/ls';

import { getFirebaseToken } from 'FirebaseInstance';
import { CONFIG } from 'shared/config';
import { ROUTES } from 'shared/constants/ROUTES';
import { talentDao } from 'shared/dao/talentDao';
import { UserType } from 'shared/enums/UserType';
import { ITalentAttributes } from 'shared/interfaces/ITalent';
import { IUserAttributes } from 'shared/interfaces/IUserCalendar';
import { errorResponseToArray } from 'shared/utils/room-shared';
import { updateSentryFeedbackFormInputs } from '../../../lib/sentry';
import { AppThunk, RootState } from '../store';
import { setGuardian } from './app.slicer';

const {
  getUserProfile: getUserProfileService,
  login,
  signup,
  sendEmail,
  resetPassword,
  notificationSubscription,
} = authDao();

const { impersonateTalent } = talentDao();

const { setLS, getLS, removeLS, clearLS } = ls();

interface ITalentMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  picture: string;
}

type MergedAttributes = ITalentAttributes & IUserAttributes;

interface IUserTalent {
  id: string;
  type: string;
  attributes: MergedAttributes;
}

export interface UserState {
  isLoading: boolean;
  user: IUserTalent | IUser | null;
  errorMessage: string | null;
  responseMessage: string | null;
  isLoggedIn: boolean;
  familyMember: ITalentMember[];
}
const initialState: UserState = {
  isLoading: false,
  user: null,
  errorMessage: null,
  responseMessage: null,
  isLoggedIn: false,
  familyMember: [],
};
export const userSlicer = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setFamilyMember(state: UserState, action: PayloadAction<{ members: ITalentMember[] }>) {
      state.familyMember = action.payload.members;
    },
    setUser(state: UserState, action: PayloadAction<{ user: IUser }>) {
      state.user = action.payload.user;
    },
    setIsLoading(state: UserState, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setErrorMessage(state: UserState, action: PayloadAction<string | null>) {
      state.errorMessage = action.payload;
    },
    setResponseMessage(state: UserState, action: PayloadAction<string | null>) {
      state.responseMessage = action.payload;
    },
    setIsLoggedIn(state: UserState, action: PayloadAction<boolean>) {
      state.isLoggedIn = action.payload;
    },
    handleCompleteProfileSuccess(state: UserState, action: PayloadAction<{ user: IUser }>) {
      const { user } = action.payload;
      setLS('is_completed_primary_details', true);
      state.isLoggedIn = true;
      state.user = user;
    },
    handleAuthExpired(state: UserState) {
      removeLS('auth_token');
      state.errorMessage = null;
      state.isLoggedIn = false;
    },
  },
});

export const selectUserState = (state: RootState) => state.user;
export const selectUser = (state: RootState) => state.user.user;

export const {
  setFamilyMember,
  setUser,
  setIsLoading,
  setErrorMessage,
  setResponseMessage,
  setIsLoggedIn,
  handleAuthExpired,
  handleCompleteProfileSuccess,
} = userSlicer.actions;
export default userSlicer.reducer;

const notifSubscription = (userID: string) => {
  console.log('Firebase notification subscription to: ', `talent-${userID}`);
  getFirebaseToken().then((res) =>
    notificationSubscription({ topic: `talent-${userID}`, registration_tokens: [res || ''] }),
  );
};

export const impersonate =
  (id: string): AppThunk =>
  async (dispatch) => {
    const { data: user, token, refresh_token } = await impersonateTalent(id);

    setLS('auth_token', token);
    setLS('user', JSON.stringify(user));
    setLS('is_completed_primary_details', true);
    setLS('refresh_token', refresh_token);
    const prevURL = getLS('prev_url');

    setTimeout(() => {
      if (prevURL) {
        window.location.href = prevURL;
      } else window.location.reload();
    }, 100);
    return user;
  };

export const userLogin =
  (payload: ISignInRequestPayload, isRememberMe?: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const {
        data: { data: user, token, refresh_token },
      } = await login(payload);
      if (user.attributes.user_types.includes('casting')) {
        console.log('here');
        window.location.href = `${CONFIG.CASTING_APP}/login?token=${token}`;
      } else if (user.attributes.user_types.includes('talent')) {
        setLS('auth_token', token);

        setLS('is_completed_primary_details', user.attributes.completed_primary_details);
        notifSubscription(user.id);
        if (isRememberMe) {
          setLS('refresh_token', refresh_token);
        }

        // Update Sentry feedback form inputs
        updateSentryFeedbackFormInputs(user.attributes.email, user.attributes.first_name, user.attributes.last_name);
        dispatch(setIsLoggedIn(true));
        dispatch(setErrorMessage(null));
        const { data } = await getUserProfileService();
        dispatch(setUser({ user: data }));
        setLS('user', JSON.stringify(data));
        return data;
      } else if (user.attributes.user_types.some((userType) => userType === UserType.STAFF_AGENCY)) {
        window.location.href = `${CONFIG.AGENCY_APP_URL}/login?token=${token}`;
      } else if (
        user.attributes.user_types.some(
          (userType) => userType === UserType.CASTING || userType === UserType.CONTACT || userType === UserType.STAFF,
        )
      ) {
        window.location.href = `${CONFIG.CASTING_APP}/login?token=${token}`;
      } else {
        dispatch(setErrorMessage('Invalid email or password'));
        return null;
      }
    } catch (err: any) {
      dispatch(setErrorMessage('Invalid email or password'));
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const invitedUserSignup =
  (payload: IResetPasswordRequestPayload, history: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));

      const { message, token, data, refresh_token } = await resetPassword({
        ...payload,
        // completed_primary_details: false,
      });
      setLS('auth_token', token);
      setLS('user', JSON.stringify(data));
      setLS('refresh_token', refresh_token);
      setLS('is_completed_primary_details', data.attributes.completed_primary_details);
      dispatch(setResponseMessage(message));

      dispatch(setIsLoggedIn(true));
      dispatch(setErrorMessage(null));

      if (data.attributes.completed_primary_details && token) return history.push(ROUTES.TALENT.DASHBOARD);
      history.push(ROUTES.REGISTRATION);
      return message;
    } catch (err: any) {
      const { errors = {} } = err.response.data;
      const errrMessage = errorResponseToArray(errors);

      dispatch(setErrorMessage(errrMessage));
      return errrMessage;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const userSignup =
  (payload: ISignUpRequestPayload, history: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));

      const {
        data: { data: user, token },
      } = await signup({ ...payload, completed_primary_details: false, joined_via: 'web' });

      setLS('auth_token', token);
      setLS('user', JSON.stringify(user));
      setLS('is_completed_primary_details', user.attributes.completed_primary_details);

      dispatch(setUser({ user }));
      dispatch(setIsLoggedIn(true));
      dispatch(setErrorMessage(null));
      history.push(ROUTES.REGISTRATION);
      return user;
    } catch (err: any) {
      const { errors = {} } = err.response.data;
      const errrMessage = errorResponseToArray(errors);

      if (errrMessage === 'email: has already been taken') {
        const loginPayload: ISignInRequestPayload = {
          user: {
            email: payload.email,
            password: payload.password,
          },
        };
        dispatch(userLogin(loginPayload, false));
      }

      dispatch(setErrorMessage(errrMessage));
      return errrMessage;
    } finally {
      dispatch(setIsLoading(false));
    }
  };
export const userSendEmail =
  (payload: IForgotPasswordRequestPayload): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));

      const {
        data: { message = 'Email successfully sent' },
      } = await sendEmail(payload);

      dispatch(setResponseMessage(message));

      return message;
    } catch (err: any) {
      const { errors = '' } = err.response.data;
      dispatch(setErrorMessage(errors));
    } finally {
      dispatch(setIsLoading(false));
    }
  };
export const userResetPassword =
  (payload: IResetPasswordRequestPayload): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const { message, token, data, refresh_token } = await resetPassword(payload);
      setLS('auth_token', token);
      setLS('user', JSON.stringify(data));
      setLS('refresh_token', refresh_token);
      setLS('is_completed_primary_details', data.attributes.completed_primary_details);
      dispatch(setResponseMessage(message));
      return message;
    } catch (err: any) {
      const { errors = {} } = err.response.data;
      const errrMessage = errorResponseToArray(errors);

      dispatch(setErrorMessage(errrMessage));
    } finally {
      dispatch(setIsLoading(false));
    }
  };
export const getUserProfile = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setIsLoading(true));
    const { data } = await getUserProfileService();
    setLS('is_completed_primary_details', data.attributes.completed_primary_details);
    dispatch(setUser({ user: data }));
    dispatch(setIsLoggedIn(true));
  } catch (err: any) {
    // dispatch(setErrorMessage(err.response.data.error));

    dispatch(setIsLoggedIn(false));
  } finally {
    dispatch(setIsLoading(false));
  }
};

export const autoLogin =
  (payload: string): AppThunk =>
  async (dispatch) => {
    try {
      removeLS('auth_token');
      removeLS('user');
      removeLS('is_completed_primary_details');
      dispatch(setIsLoggedIn(false));
      const { data } = await getUserProfileService(payload);
      setLS('auth_token', payload);
      setLS('user', JSON.stringify(data));
      setLS('is_completed_primary_details', data.attributes.completed_primary_details);
      notifSubscription(data.id);
      dispatch(setUser({ user: data }));
      dispatch(setIsLoggedIn(true));
    } catch (err: any) {
      window.location.href = '/login';
    }
  };

export const userLogout = (): AppThunk => async (dispatch) => {
  try {
    dispatch(
      setGuardian({
        isGuardian: false,
        guardianUI: false,
        members: [],
      }),
    );
    clearLS();
    dispatch(setIsLoggedIn(false));
    window.location.reload();
  } catch (err: any) {
    // dispatch(setErrorMessage(err.response.data.message));
  } finally {
    // dispatch(setIsLoading(false));
  }
};
